import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { Shield, Mail, Lock, Eye, EyeOff, ArrowLeft, MailCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import RegisterWizard from "../components/RegisterWizard";
import TwoFactorChallenge from "../components/TwoFactorChallenge";
import { toast } from "sonner";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { extractError } from "../lib/errors";

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });

  // S14b: estado del challenge de 2FA (en lugar de JWT inmediato)
  const [twoFAChallenge, setTwoFAChallenge] = useState(null); // {challenge_token, challenge_expires_at}

  // Handle email-confirmation deep link: /auth/confirm?token=...
  useEffect(() => {
    const path = location.pathname;
    const token = searchParams.get("token");
    if (path.endsWith("/auth/confirm") && token) {
      axios
        .get(`${API}/auth/confirm`, { params: { token } })
        .then(() => toast.success("Email confirmed. You can now sign in."))
        .catch((err) =>
          toast.error(extractError(err, "Confirmation failed")),
        );
    }
  }, [location.pathname, searchParams]);

  if (user) {
    const from = location.state?.from?.pathname || "/dashboard";
    navigate(from, { replace: true });
    return null;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginForm);
      // S14b: si el backend responde con requires_2fa, mostrar el challenge
      if (response.data?.requires_2fa) {
        setTwoFAChallenge({
          challenge_token: response.data.challenge_token,
          challenge_expires_at: response.data.challenge_expires_at,
        });
        return;
      }
      login(response.data.user, response.data.token);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (error) {
      const status = error.response?.status;
      if (status === 403) {
        toast.error(
          error.response?.data?.detail || "Please confirm your email before signing in",
        );
      } else if (status === 429) {
        toast.error("Too many attempts. Try again in a minute.");
      } else {
        toast.error("Invalid credentials");
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FASuccess = ({ token, user: userData }) => {
    login(userData, token);
    toast.success("Welcome back!");
    navigate("/dashboard");
  };

  const handle2FAExpired = () => {
    setTwoFAChallenge(null);
    toast.error(t("auth.2fa.challenge.expired"));
  };

  const handleRegister = async (payload) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, payload);
      if (response.data?.status === "confirmation_pending") {
        setPendingEmail(response.data.email);
        toast.success("Check your inbox to confirm your email.");
      } else if (response.data?.token) {
        login(response.data.user, response.data.token);
        toast.success("Account created.");
        // S14b: default-on 2FA para nuevos signups — redirigir al wizard (con skip visible)
        navigate("/account/2fa?setup=onboarding");
      }
    } catch (error) {
      const status = error.response?.status;
      if (status === 429) toast.error("Too many attempts. Try again in a minute.");
      else toast.error(extractError(error, "Registration failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!pendingEmail) return;
    try {
      await axios.post(`${API}/auth/resend-confirmation`, { email: pendingEmail });
      toast.success("Confirmation email re-sent.");
    } catch {
      toast.error("Could not resend right now. Try again in a minute.");
    }
  };

  // Show confirmation-pending screen
  if (pendingEmail) {
    return (
      <div className="dark min-h-screen flex items-center justify-center bg-[#0a0a0a] p-6">
        <Card className="w-full max-w-md border-border shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="w-12 h-12 rounded-xl bg-primary mx-auto flex items-center justify-center mb-4">
              <MailCheck className="w-7 h-7 text-primary-foreground" />
            </div>
            <CardTitle className="text-2xl font-heading">Check your inbox</CardTitle>
            <CardDescription>
              We sent a confirmation link to <strong>{pendingEmail}</strong>.
              Please confirm your email before signing in.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button onClick={handleResend} variant="outline" className="w-full">
              Resend confirmation email
            </Button>
            <Button onClick={() => setPendingEmail(null)} variant="ghost" className="w-full">
              Use a different email
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // S14b: mostrar formulario de segundo factor si hay un challenge activo
  if (twoFAChallenge) {
    return (
      <div className="dark min-h-screen flex items-center justify-center bg-[#0a0a0a] p-6">
        <div className="w-full max-w-md">
          <Button
            variant="ghost"
            className="mb-6"
            onClick={() => setTwoFAChallenge(null)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("auth.2fa.challenge.back_to_login")}
          </Button>
          <Card className="border-border shadow-lg">
            <CardHeader className="text-center pb-2">
              <div className="w-12 h-12 rounded-xl bg-primary mx-auto flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-primary-foreground" />
              </div>
              <CardTitle className="text-2xl font-heading">ComplianceOS</CardTitle>
            </CardHeader>
            <CardContent>
              <TwoFactorChallenge
                challengeToken={twoFAChallenge.challenge_token}
                expiresAt={twoFAChallenge.challenge_expires_at}
                onSuccess={handle2FASuccess}
                onExpired={handle2FAExpired}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="dark min-h-screen flex items-center justify-center bg-[#0a0a0a] p-6" data-testid="auth-page">
      <div className="w-full max-w-md">
        <Button variant="ghost" className="mb-6" onClick={() => navigate("/")} data-testid="back-to-landing">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t("auth.back_to_home")}
        </Button>

        <Card className="border-border shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="w-12 h-12 rounded-xl bg-primary mx-auto flex items-center justify-center mb-4">
              <Shield className="w-7 h-7 text-primary-foreground" />
            </div>
            <CardTitle className="text-2xl font-heading">ComplianceOS</CardTitle>
            <CardDescription>by MSSPRD &middot; Sign in or create an account</CardDescription>
          </CardHeader>

          <CardContent>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="login-tab">{t("auth.sign_in")}</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">{t("auth.sign_up")}</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input id="login-email" type="email" placeholder="you@company.com" className="pl-10"
                        value={loginForm.email}
                        onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                        required autoComplete="email" data-testid="login-email-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input id="login-password" type={showPassword ? "text" : "password"}
                        placeholder="********" className="pl-10 pr-10"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        required autoComplete="current-password" data-testid="login-password-input" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        aria-pressed={showPassword}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none rounded">
                        {showPassword ? <EyeOff className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
                    {loading ? t("auth.signing_in") : t("auth.sign_in")}
                  </Button>
                  <div className="text-center mt-2">
                    <Link
                      to="/auth/forgot"
                      className="text-xs text-muted-foreground hover:text-primary underline-offset-4 hover:underline"
                      data-testid="forgot-password-link"
                    >
                      {t("auth.forgot.link_in_login")}
                    </Link>
                  </div>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <RegisterWizard onSubmit={handleRegister} loading={loading} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthPage;
